import React from 'react';
import styles from './RegisteredUsersDisplay.module.css';

interface User {
  User_ID: string;
  firstname: string;
  lastname: string;
  title?: string;
  mail: string;
  jobtitle?: string;
  picture?: string;
  profile?: string;
  linkedin?: string;
  firm?: {
    Firm_ID: string;
    abbrev: string;
    name?: string;
    logo?: string;
  };
}

interface Registration {
  Registration_ID: string;
  User_ID: string;
  Event_ID: string;
  created_at: string;
}

interface RegisteredUsersDisplayProps {
  registration: Registration[];
  users: User[];
}

const RegisteredUsersDisplay: React.FC<RegisteredUsersDisplayProps> = ({ 
  registration, 
  users 
}) => {
  // Get registered users
  const getRegisteredUsers = (): User[] => {
    return registration
      .map(reg => users.find(user => user.User_ID === reg.User_ID))
      .filter((user): user is User => user !== undefined);
  };

  const registeredUsers = getRegisteredUsers();

  const handleEmailClick = (email: string) => {
    window.location.href = `mailto:${email}`;
  };

  const handleWebsiteClick = (url: string) => {
    if (url && url !== "None") {
      // Ensure URL has protocol
      const fullUrl = url.startsWith('http') ? url : `https://${url}`;
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleLinkedInClick = (url: string) => {
    if (url && url !== "None") {
      // Ensure URL has protocol
      const fullUrl = url.startsWith('http') ? url : `https://${url}`;
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className={styles.viewContainer}>
      <div className={styles.viewHeader}>
        <h3 className={styles.viewTitle}>Registrations</h3>
      </div>
      
      <div className={styles.userCircleContainer}>
        {registeredUsers.map((user) => (
          <div key={user.User_ID} className={styles.userCircle}>
            <div className={styles.userCircleHeader}>
              {user.picture ? (
                <img 
                  src={`data:image/png;base64,${user.picture}`} 
                  className={styles.userImage} 
                  alt={`${user.firstname} ${user.lastname}`}
                />
              ) : (
                <img 
                  src="/static/images/user-profile-icon.png" 
                  className={styles.userImage} 
                  alt={`${user.firstname} ${user.lastname}`}
                />
              )}
            </div>
            
            <div className={styles.userCircleBody}>
              <h2 className={styles.userName}>
                {user.title && `${user.title} `}
                {user.firstname} {user.lastname}
              </h2>
              <p className={styles.userPosition}>
                {user.jobtitle || ''}
              </p>
              <p className={styles.userFirm}>
                {user.firm && user.firm.abbrev && (
                  <>
                    <i className="bi bi-building"></i> {user.firm.abbrev}
                  </>
                )}
              </p>
              <span className={styles.userLocation}>
                <strong>Hamburg</strong>
              </span>
            </div>
            
            <div className={styles.userCircleFooter}>
              <div className={styles.actionsWrapper}>
                <div className={styles.userSocial}>
                  <button
                    className={`${styles.socialIcon} ${styles.mailIcon}`}
                    onClick={() => handleEmailClick(user.mail)}
                    title="Send Email"
                  >
                    <i className="bi bi-envelope"></i> Email
                  </button>
                  
                  {user.profile && user.profile !== "None" && (
                    <>
                      <div className={styles.divider}></div>
                      <button
                        className={`${styles.socialIcon} ${styles.profileIcon}`}
                        onClick={() => handleWebsiteClick(user.profile!)}
                        title="Web Profile"
                      >
                        <i className="bi bi-globe"></i> Web
                      </button>
                    </>
                  )}
                  
                  {user.linkedin && user.linkedin !== "None" && (
                    <>
                      <div className={styles.divider}></div>
                      <button
                        className={`${styles.socialIcon} ${styles.linkedinIcon}`}
                        onClick={() => handleLinkedInClick(user.linkedin!)}
                        title="LinkedIn Profile"
                      >
                        <i className="bi bi-linkedin"></i> LinkedIn
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RegisteredUsersDisplay;
